import React, { useCallback, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import api, { cooperplaceApi } from 'services/api';
import { validateCities, validateOptions } from './contract';

export const selectProps = {
  isMulti: true,
  closeMenuOnSelect: false,
  hideSelectedOptions: true,
  classNamePrefix: 'tc-f-select',
  placeholder: 'Todos',
  noOptionsMessage: () => 'Nenhuma opção encontrada',
  loadingMessage: () => 'Buscando opções…',
  styles: { menuPortal: (base) => ({ ...base, zIndex: 10020 }) },
};
export default function OptionField({
  id,
  label,
  value,
  onChange,
  catalog,
  options,
  cache,
  remember,
  hint,
  scope,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ rows: [], loading: false, error: null, hasMore: false });
  const abortRef = useRef(null);
  const selectRef = useRef(null);
  const generation = useRef(0);
  const optionsById = { ...cache, ...Object.fromEntries((options || []).map((o) => [o.value, o])) };
  const selected = value.map(
    (v) => optionsById[v] || { value: v, label: `Opção #${v} (indisponível)` },
  );
  const unresolved = value.filter((v) => !optionsById[v]);
  const unresolvedKey = JSON.stringify(unresolved);
  const city = catalog === 'cities';
  // Reabrir conserva seleções antigas mesmo se o catálogo disponível mudou.
  useEffect(() => {
    if (!catalog || unresolvedKey === '[]') return;
    const abort = new AbortController();
    const ids = JSON.parse(unresolvedKey);
    const fetch = city
      ? api
          .get('cities', { params: { country: 'br' }, signal: abort.signal })
          .then((r) => validateCities(r.data).filter((o) => ids.includes(o.value)))
      : cooperplaceApi
          .get('control-tower/load-filter-options', {
            params: { field: catalog, scope, ids: JSON.stringify(ids), page: 1, perPage: 100 },
            signal: abort.signal,
          })
          .then((r) => validateOptions(r.data, catalog, 1, 100).data);
    fetch
      .then((rows) => {
        if (!abort.signal.aborted) remember(id, rows);
      })
      .catch(() => {
        if (!abort.signal.aborted)
          setState((s) => ({
            ...s,
            error:
              'Não foi possível recuperar os nomes selecionados. Os filtros foram preservados.',
          }));
      });
    return () => abort.abort();
  }, [catalog, city, scope, unresolvedKey, remember, id, retry]);
  useEffect(() => {
    if (!catalog || !open) return undefined;
    const current = ++generation.current;
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;
    if (city && search.trim().length < 2) {
      setState({ rows: [], loading: false, error: null, hasMore: false });
      return () => abort.abort();
    }
    setState((s) => ({
      ...s,
      rows: page === 1 ? [] : s.rows,
      loading: true,
      error: null,
      hasMore: false,
    }));
    const timer = setTimeout(async () => {
      try {
        let rows, hasMore;
        if (city) {
          const result = await api.get('cities', {
            params: { country: 'br', search: search.trim() },
            signal: abort.signal,
          });
          const all = validateCities(result.data);
          rows = all.slice((page - 1) * 25, page * 25);
          hasMore = page * 25 < all.length;
        } else {
          const result = await cooperplaceApi.get('control-tower/load-filter-options', {
            params: { field: catalog, scope, search: search.trim(), page, perPage: 25 },
            signal: abort.signal,
          });
          ({
            data: rows,
            pagination: { hasMore },
          } = validateOptions(result.data, catalog, page, 25));
        }
        if (abort.signal.aborted || generation.current !== current) return;
        remember(id, rows);
        setState((s) => ({
          rows: [
            ...new Map([...(page === 1 ? [] : s.rows), ...rows].map((o) => [o.value, o])).values(),
          ],
          loading: false,
          error: null,
          hasMore,
        }));
      } catch {
        if (!abort.signal.aborted && generation.current === current)
          setState((s) => ({
            ...s,
            loading: false,
            error: 'Não foi possível buscar opções. Tente novamente.',
            hasMore: false,
          }));
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [catalog, city, id, open, page, search, retry, remember, scope]);
  const input = useCallback((text, { action }) => {
    if (action === 'input-change') {
      setSearch(text.slice(0, 100));
      setPage(1);
    } else if (action === 'set-value' || action === 'menu-close') {
      setSearch('');
      setPage(1);
    }
  }, []);
  return (
    <div className="tc-f-field" data-filter-field={id}>
      <label htmlFor={id}>{label}</label>
      <Select
        {...selectProps}
        ref={selectRef}
        inputId={id}
        instanceId={id}
        aria-label={label}
        aria-describedby={hint ? `${id}-hint` : undefined}
        value={selected}
        options={options || state.rows}
        onChange={(items) => onChange(items.map((o) => o.value))}
        onMenuOpen={() => setOpen(true)}
        onMenuClose={() => setOpen(false)}
        onInputChange={catalog ? input : undefined}
        inputValue={catalog ? search : undefined}
        isLoading={state.loading}
        menuIsOpen={catalog ? open : undefined}
        tabSelectsValue={false}
        filterOption={catalog ? null : undefined}
        menuPortalTarget={document.body}
        noOptionsMessage={() =>
          city && search.trim().length < 2
            ? 'Digite ao menos 2 letras da cidade'
            : state.error
              ? 'Busca indisponível'
              : 'Nenhuma opção encontrada'
        }
        isOptionDisabled={(option) => value.length >= 100 && !value.includes(option.value)}
      />
      {catalog && state.hasMore && (
        <button
          type="button"
          className="tc-f-more"
          aria-label={`Carregar mais opções de ${label}`}
          disabled={state.loading}
          onClick={() => {
            setPage((p) => p + 1);
            setOpen(true);
            selectRef.current?.focus();
          }}
        >
          Carregar mais opções
        </button>
      )}
      {hint && <small id={`${id}-hint`}>{hint}</small>}
      {state.error && (
        <div className="tc-f-search-error" role="alert">
          {state.error}{' '}
          <button
            type="button"
            onClick={() => {
              setOpen(true);
              setRetry((n) => n + 1);
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}
    </div>
  );
}
